// @ts-strict-ignore
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { AutomationAutoStagedRollout, GoogleAppStoreSettings, RunwayResponseType } from '../../types';
import * as React from 'react';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../../typescriptHelpers';
import { selectAutomationById, selectIsReleaseRollback } from '../../selectors/selectors';
import { globalMessage } from '../../utils/globalMessage';
import EditGoogleReleaseSettings from './../appSettings/EditGoogleReleaseSettings';
import { ReleaseUpdatable } from '../../actions/updatable';
import { tryUpdateRelease, UpdateGoogleAppStoreReleaseSettingsRequestForReleaseStep } from '../../actions/release';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { getRolloutDaysToPercentageFromAutomation } from '@/helpers';
import { StepRunUpdatable } from '@/actions/stepRun';

interface EditGoogleReleaseSettingsContainerProps extends ReleaseComponentProps {
    open: boolean;
    displayableVersion: string;
    isReleased: boolean;
    releaseSettings?: GoogleAppStoreSettings;
    onClose: () => void;
    onSuccess: () => void;
    autoStagedRolloutAutomationId: Nullable<string>;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

const EditGoogleReleaseSettingsContainer: React.FC<EditGoogleReleaseSettingsContainerProps> = (
    props: EditGoogleReleaseSettingsContainerProps,
) => {
    const dispatch = useDispatch();

    const autoStagedRolloutAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, props.appId, props.autoStagedRolloutAutomationId),
    ) as AutomationAutoStagedRollout;

    const customStagedRolloutDaysToPercent = getRolloutDaysToPercentageFromAutomation(autoStagedRolloutAutomation);

    const customStagedRolloutEnabledAndActive = useSelector((state: RootState) => {
        const isEnabled = autoStagedRolloutAutomation?.enabled;

        if (props.releaseId) {
            const isRollback = selectIsReleaseRollback(state, props.releaseId);

            return isEnabled && !isRollback;
        }

        return isEnabled;
    });

    const [
        updateAppStoreSettingsStepRun,
        updateAppStoreSettingsIsLoadingStepRun,
        updateAppStoreSettingsResponseTypeStepRun,
        updateAppStoreSettingsErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.UpdateAppStoreSettings, props.stepRunComponentProps);

    const releaseSettingsUpdatedSuccessReleaseStep = useUpdated(ReleaseUpdatable.UpdateGoogleReleaseSettings);
    const releaseSettingsUpdateLoadingReleaseStep = useIsLoading(ReleaseUpdatable.UpdateGoogleReleaseSettings);
    const releaseSettingsUpdateErrorReleaseStep = useError(ReleaseUpdatable.UpdateGoogleReleaseSettings);

    const releaseSettingsUpdatedSuccess =
        props.stepRunComponentProps ?
            updateAppStoreSettingsResponseTypeStepRun === RunwayResponseType.Success
        :   releaseSettingsUpdatedSuccessReleaseStep;
    const releaseSettingsUpdateLoading =
        props.stepRunComponentProps ? updateAppStoreSettingsIsLoadingStepRun : releaseSettingsUpdateLoadingReleaseStep;
    const releaseSettingsUpdateError =
        props.stepRunComponentProps ? updateAppStoreSettingsErrorStepRun : releaseSettingsUpdateErrorReleaseStep;

    const onSaveChanges = (
        stagedRelease: boolean,
        inAppUpdatePriority: number,
        initialStagedRolloutPercentage?: number,
    ) => {
        const request: UpdateGoogleAppStoreReleaseSettingsRequestForReleaseStep = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
            stagedRelease,
            initialStagedRolloutPercentage,
            inAppUpdatePriority,
            rolloutPercentage: {
                fraction: initialStagedRolloutPercentage ? initialStagedRolloutPercentage / 100 : 0,
            },
        };

        if (props.stepRunComponentProps) {
            updateAppStoreSettingsStepRun(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.UpdateGoogleReleaseSettings, request));
        }
    };

    React.useEffect(() => {
        if (releaseSettingsUpdatedSuccess) {
            globalMessage.success('Release settings updated!');

            props.onSuccess();
        }
    }, [releaseSettingsUpdatedSuccess]);

    React.useEffect(() => {
        if (releaseSettingsUpdateError) {
            globalMessage.error(releaseSettingsUpdateError);
        }
    }, [releaseSettingsUpdateError]);

    return (
        <EditGoogleReleaseSettings
            open={props.open}
            isConfiguringAppDefaults={false}
            isWorkflowStep={!!props.stepRunComponentProps}
            displayableVersion={props.displayableVersion}
            isLoading={releaseSettingsUpdateLoading}
            customStagedRolloutEnabled={customStagedRolloutEnabledAndActive}
            customStagedRolloutDaysToPercent={customStagedRolloutDaysToPercent}
            googleAppStoreSettings={props.releaseSettings}
            isReleased={props.isReleased}
            onSubmit={onSaveChanges}
            onCancel={props.onClose}
        />
    );
};

export default EditGoogleReleaseSettingsContainer;
